import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Grid, Icon } from 'semantic-ui-react';
import Loader from 'components/Loader/Loader';
import { push } from 'connected-react-router';
import { getReportsAction, getSpotMultidayPricingAction, updateReportsAction } from 'actions/actions';
import OverviewWidget from 'components/OverviewWidget/OverviewWidget';
import OccupancyWidget from 'components/OccupancyWidget/OccupancyWidget';
import PayoutWidget from 'components/PayoutWidget/PayoutWidget';
import RevenueWidget from 'components/RevenueWidget/RevenueWidget';
import DynamicPricingWidget from 'components/PricingWidget/DynamicPricingWidget';
import StandardPricingWidget from 'components/PricingWidget/StandardPricingWidget';
import EnforcementWidget from 'components/EnforcementWidget/EnforcementWidget';
import useInterval from 'hooks/useInterval';
import { AppState } from 'reducers/reducers';
import { reportForSelectedSpot, reportLastUpdatedAtForSelectedSpot, selectedSpotId, selectedSpotInfo } from 'selectors';
import { trackPageViewed } from 'utils/analytics';
import { scrollMainToTop } from 'utils/helpers';
import styles from './SpotDetailContainer.module.css';
import type { OrganizationListObj, SpotInterface } from 'types';
import { useOrganization } from 'contexts/OrganizationsContext';
import env from 'env';
import OverviewContent from 'components/Dashboard/DashboardContent/OverviewContent';
import AddSpot from 'components/Dashboard/AddSpot/AddSpot';
import PropertyInsightsWidget from 'components/PropertyInsights/PropertyInsightsWidget/PropertyInsightsWidget';
import NewOwnerOnboarding from 'components/NewOwnerOnboarding/NewOwnerOnboarding';
import OnboardingBanner from 'components/NewOwnerOnboarding/OnboardingBanner';
import { useDocuments } from 'contexts/DocumentsContext';
import { DocumentCategory } from 'types/Documents';

type SpotDetailContainerProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const MILLISECONDS_TO_UPDATE_REPORTS = env.MINUTES_TO_UPDATE_REPORTS * 60 * 1000;

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;
const ONBOARDING_WINDOW_DAYS = 30;

const SpotDetailContainer = ({
    orgSpots,
    orgSpotsLoading,
    organization,
    spotId,
    currentSpot,
    report,
    reportError,
    isReportLoading,
    multidayRates,
    loadingRates,
    rateErrorMessage,
    profile,
    updateReportsLoading,
    lastUpdatedAt,
    isDemoMode,
    push: connectedPush,
    getReports,
    getMultidayPricing,
    updateReports,
}: SpotDetailContainerProps): JSX.Element => {
    // @TODO: Merge role into the "whole" organization object.
    const { organization: organizationRole } = useOrganization();
    const { documents, categories, loading: docsLoading, getDocuments, uploadDocuments } = useDocuments();
    const [showEnforcementWidget, setShowEnforcementWidget] = useState(false);
    const [onboardingMode, setOnboardingMode] = useState<boolean | null>(null);

    const isInOnboardingWindow = useMemo(() => {
        if (!currentSpot?.launch_date) return false;
        const launchDate = new Date(currentSpot.launch_date);
        return (Date.now() - launchDate.getTime()) / MILLISECONDS_PER_DAY < ONBOARDING_WINDOW_DAYS;
    }, [currentSpot?.launch_date]);

    const hasW9 = useMemo(
        () => documents?.some((d) => d.category?.category === DocumentCategory.TAX_DOCUMENT),
        [documents]
    );

    // Reset frozen mode when the user navigates to a different spot.
    useEffect(() => {
        setOnboardingMode(null);
    }, [currentSpot?.pk]);

    // Fetch documents only for recently-launched spots.
    useEffect(() => {
        if (currentSpot?.uuid && isInOnboardingWindow) {
            getDocuments(currentSpot.uuid);
        }
    }, [currentSpot?.uuid, getDocuments]);

    // Freeze the onboarding mode once after the first document fetch completes.
    useEffect(() => {
        if (onboardingMode !== null || docsLoading || (isInOnboardingWindow && documents === null)) return;
        setOnboardingMode(!hasW9 && isInOnboardingWindow);
    }, [docsLoading, isInOnboardingWindow, hasW9, documents, onboardingMode]);

    useInterval({
        callback: updateReports,
        intervalTime: MILLISECONDS_TO_UPDATE_REPORTS,
        lastCalledAt: lastUpdatedAt,
    });

    useEffect(() => {
        trackPageViewed({
            pageName: 'Spot Detail',
            organizationId: organization?.pk,
            spotId,
            spot_detail_page_version: 'v3',
            affiliationRole: organizationRole?.role,
            superuser: profile.superuser,
        });
    }, [profile.superuser, organization?.pk, organizationRole?.role, spotId]);

    useEffect(() => {
        if (currentSpot && !report) getReports();
    }, [currentSpot, getReports, report]);

    useEffect(() => {
        if (report) setShowEnforcementWidget(report.enforcement_uplift.year_to_date.enforcement_revenue > 0);
    }, [report]);

    useEffect(() => {
        if (currentSpot?.dynamic_pricing_enabled) {
            getMultidayPricing(6);
        }
    }, [currentSpot, getMultidayPricing]);

    if (orgSpots === null || orgSpotsLoading || onboardingMode === null) {
        return <Loader />;
    }
    // From now on, the spot object is non-null; since the selector uses the orgSpots.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const spotArray = Object.values(orgSpots!);
    if (spotArray.length === 0 || !currentSpot) {
        return <></>;
    }

    function navigate(url: string) {
        connectedPush(url);
        scrollMainToTop();
    }

    const onSelectSpot = (spot: SpotInterface) => {
        if (spot.pk !== currentSpot.pk) {
            navigate(`/organization/${organization.pk}/spots/${spot.pk}/detail`);
        }
    };

    const onSelectOrganization = (org: OrganizationListObj) => {
        if (org.pk !== organization.pk) {
            navigate(`/organization/${org.pk}/`);
        }
    };

    if (onboardingMode === true) {
        return (
            <OverviewContent
                currentSpot={currentSpot}
                orgSpots={spotArray}
                onSelectSpot={onSelectSpot}
                onSelectOrganization={onSelectOrganization}
                organization={organization}
                isDemoMode={isDemoMode}
            >
                <NewOwnerOnboarding
                    spot={currentSpot}
                    navigateWithinOrg={(path) => navigate(`/organization/${organization.pk}${path}`)}
                    setOnboardingMode={setOnboardingMode}
                />
            </OverviewContent>
        );
    }

    return (
        <OverviewContent
            currentSpot={currentSpot}
            orgSpots={spotArray}
            onSelectSpot={onSelectSpot}
            onSelectOrganization={onSelectOrganization}
            organization={organization}
            isDemoMode={isDemoMode}
        >
            {isInOnboardingWindow && onboardingMode === false && (
                <OnboardingBanner
                    documents={documents || []}
                    categories={categories}
                    isLoading={docsLoading}
                    spot={currentSpot}
                    uploadDocuments={uploadDocuments}
                />
            )}

            <Grid stackable>
                <Grid.Row>
                    <Grid.Column computer={6} tablet={16}>
                        <PayoutWidget organization={organization} />
                        <OverviewWidget
                            reportError={reportError}
                            report={report}
                            isReportLoading={isReportLoading}
                            updateReportsLoading={updateReportsLoading}
                            spotId={currentSpot.uuid}
                            spotTimeZone={currentSpot.timezone}
                        />
                        {showEnforcementWidget && (
                            <EnforcementWidget
                                report={report}
                                loading={isReportLoading}
                                updateReportsLoading={updateReportsLoading}
                            />
                        )}
                        {/* Depending on dynamic pricing being enabled we should render OccupancyWidget here or on the other column */}
                        {currentSpot.dynamic_pricing_enabled && currentSpot.display_price_monthly && (
                            <OccupancyWidget spot={currentSpot} report={report} />
                        )}
                        <PropertyInsightsWidget
                            spot={currentSpot}
                            navigate={(path) =>
                                navigate(`/organization/${organization.pk}/spots/${currentSpot.pk}${path}`)
                            }
                        />

                        <div className={styles.timezoneWrapperLarge}>
                            <Icon name="clock outline" />
                            <span>Timezone: {currentSpot.timezone}</span>
                        </div>
                    </Grid.Column>
                    <Grid.Column computer={10} tablet={16}>
                        <RevenueWidget
                            report={report}
                            isReportLoading={isReportLoading}
                            reportError={reportError}
                            updateReportsLoading={updateReportsLoading}
                        />
                        {currentSpot.dynamic_pricing_enabled ? (
                            <DynamicPricingWidget
                                spot={currentSpot}
                                multidayRates={multidayRates}
                                loadingRates={loadingRates}
                                rateErrorMessage={rateErrorMessage}
                                report={report}
                                isReportLoading={isReportLoading}
                                updateReportsLoading={updateReportsLoading}
                            />
                        ) : (
                            <>
                                <StandardPricingWidget spot={currentSpot} />
                                {/* Depending on dynamic pricing being enabled we should render OccupancyWidget here or on the other column */}
                                {currentSpot.display_price_monthly && (
                                    <OccupancyWidget spot={currentSpot} layout="horizontal" report={report} />
                                )}
                            </>
                        )}
                    </Grid.Column>
                </Grid.Row>
                <div className={styles.timezoneWrapperSmall}>
                    <Grid.Row>
                        <Grid.Column>
                            <Icon name="clock outline" />
                            <span>Timezone: {currentSpot.timezone}</span>
                        </Grid.Column>
                    </Grid.Row>
                </div>
                <AddSpot organizationId={organization.pk} />
            </Grid>
        </OverviewContent>
    );
};

function mapStateToProps(state: AppState) {
    return {
        isDemoMode: state.dashboard.isDemoMode,
        orgSpots: state.spots.orgSpots,
        orgSpotsLoading: state.spots.orgSpotsLoading,
        currentSpot: selectedSpotInfo(state),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        spotId: selectedSpotId(state)!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        organization: state.organization.organization!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        profile: state.auth.profile!,
        report: reportForSelectedSpot(state),
        reportError: state.reports.errorMessage,
        isReportLoading: state.reports.loading,
        multidayRates: state.spots.spotMultidayPricing,
        loadingRates: state.spots.spotMultidayPricingLoading,
        rateErrorMessage: state.spots.spotMultidayPricingErrorMessage,
        updateReportsLoading: state.reports.loadingUpdate,
        lastUpdatedAt: reportLastUpdatedAtForSelectedSpot(state),
    };
}

const mapDispatchToProps = {
    push,
    getReports: getReportsAction,
    getMultidayPricing: getSpotMultidayPricingAction,
    updateReports: updateReportsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(SpotDetailContainer);
